import { Data, Equal, Match } from 'effect';
import { isNil } from 'lodash';
import { DataSourceFilterEnum } from '../types';
import { analyticsService, type DropdownOpened } from 'venn-utils';

type UseDataSourceFilterActionsDependencies = {
  state: {
    selectedFilters: DataSourceFilterEnum[];
    integrationsCheckboxState: 'checked' | 'unchecked' | 'indeterminate';
    viewableDataSourcesEnums: ReturnType<typeof DataSourceFilterEnum.Source>[];
    originallySelectedFilters: DataSourceFilterEnum[];
  };
  mutations: {
    setOpen: (value: boolean) => void;
    setSelectedFilters: React.Dispatch<React.SetStateAction<DataSourceFilterEnum[]>>;
    onFilter: (updated: DataSourceFilterEnum[]) => void;
  };
};

export type DataSourceFilterActions = Data.TaggedEnum<{
  ToggleIntegrations: {};
  SelectAll: {};
  DeselectAll: {};
  SelectDataSource: { dataSource: DataSourceFilterEnum };
  DeselectDataSource: { dataSource: DataSourceFilterEnum };
  ApplyFilters: {};
  Cancel: {};
  OpenMenu: {};
  CloseMenu: {};
}>;
export const DataSourceFilterActions = Data.taggedEnum<DataSourceFilterActions>();

export const useDataSourceFilterActions =
  (dependencies: UseDataSourceFilterActionsDependencies | undefined) => (action: DataSourceFilterActions) => {
    if (isNil(dependencies)) {
      return;
    }
    const {
      state: { integrationsCheckboxState, viewableDataSourcesEnums, selectedFilters, originallySelectedFilters },
      mutations: { setOpen, setSelectedFilters, onFilter },
    } = dependencies;
    const setOpenWithAnalytics = (open: boolean) => {
      const analyticsProps = {
        label: 'Data Source',
        selectType: 'multiselect',
        type: 'library filter',
      } satisfies DropdownOpened;
      if (open) {
        analyticsService.dropdownOpened(analyticsProps);
      } else {
        analyticsService.dropdownClosed(analyticsProps);
      }
      setOpen(open);
    };
    const resetSelectedFilters = () => setSelectedFilters(originallySelectedFilters);
    const notSource = (item: DataSourceFilterEnum) => !DataSourceFilterEnum.$is('Source')(item);
    const notIntegration = (item: DataSourceFilterEnum) => !DataSourceFilterEnum.$is('AllIntegrations')(item);
    Match.value(action).pipe(
      Match.tag('ToggleIntegrations', () =>
        Match.value(integrationsCheckboxState).pipe(
          Match.when('checked', () => setSelectedFilters((prev) => prev.filter(notSource).filter(notIntegration))),
          Match.whenOr('unchecked', 'indeterminate', () => {
            setSelectedFilters((prev) => [
              ...prev.filter(notSource).filter(notIntegration),
              ...viewableDataSourcesEnums,
              DataSourceFilterEnum.AllIntegrations(),
            ]);
          }),
          Match.exhaustive,
        ),
      ),
      Match.tag('SelectAll', () =>
        setSelectedFilters([
          DataSourceFilterEnum.UploadedByMe(),
          DataSourceFilterEnum.UploadedByTeam(),
          DataSourceFilterEnum.VennProvided(),
          DataSourceFilterEnum.AllIntegrations(),
          ...viewableDataSourcesEnums,
        ]),
      ),
      Match.tag('DeselectAll', () => setSelectedFilters([])),
      Match.tag('SelectDataSource', ({ dataSource }) =>
        setSelectedFilters((prev) => {
          const newSelected = [...prev, dataSource];
          const allSourcesSelected =
            newSelected.filter(DataSourceFilterEnum.$is('Source')).length === viewableDataSourcesEnums.length;
          if (allSourcesSelected) {
            return [...newSelected.filter(notIntegration), DataSourceFilterEnum.AllIntegrations()];
          }
          return newSelected;
        }),
      ),
      Match.tag('DeselectDataSource', ({ dataSource }) =>
        setSelectedFilters((prev) =>
          (DataSourceFilterEnum.$is('Source')(dataSource) ? prev.filter(notIntegration) : prev).filter(
            (selected) => !Equal.equals(selected, dataSource),
          ),
        ),
      ),
      Match.tag('ApplyFilters', () => {
        setOpenWithAnalytics(false);
        onFilter(
          selectedFilters.some(DataSourceFilterEnum.$is('AllIntegrations'))
            ? selectedFilters.filter(notSource)
            : selectedFilters,
        );
      }),
      Match.tag('Cancel', () => {
        setOpenWithAnalytics(false);
        resetSelectedFilters();
      }),
      Match.tag('OpenMenu', () => {
        setOpenWithAnalytics(true);
      }),
      Match.tag('CloseMenu', () => {
        resetSelectedFilters();
        setOpenWithAnalytics(false);
      }),
      Match.exhaustive,
    );
  };
